import React, { useState, useEffect } from 'react';
import ImageLightbox from './ImageLightbox';

interface Screenshot {
  id: string | number;
  url: string;
  title: string;
  description?: string;
}

interface ScreenshotCarouselProps {
  title: string;
  screenshots: Screenshot[];
  className?: string;
}

const ScreenshotCarousel: React.FC<ScreenshotCarouselProps> = ({
  title,
  screenshots,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!screenshots || screenshots.length === 0) {
    return null;
  }

  // Auto-rotate screenshots every 3 seconds (pause when lightbox is open)
  useEffect(() => {
    if (lightboxOpen) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % screenshots.length);
        setIsTransitioning(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [screenshots.length, lightboxOpen]);

  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % screenshots.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPrevious = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + screenshots.length) % screenshots.length);
      setIsTransitioning(false);
    }, 300);
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const currentScreenshot = screenshots[currentIndex];

  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{title}</h3>

        <div className="relative">
          {/* Main Screenshot Display */}
          <div
            className={`aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden transition-opacity duration-300 cursor-pointer ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
            onClick={() => openLightbox(currentIndex)}
          >
            {currentScreenshot.url ? (
              <img
                src={currentScreenshot.url}
                alt={currentScreenshot.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">{currentScreenshot.title}</p>
              </div>
            )}
          </div>

          {/* Click to expand hint */}
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            Büyütmek için tıklayın
          </div>

          {/* Navigation Arrows */}
          {screenshots.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-900 bg-opacity-70 hover:bg-opacity-90 text-white p-2 rounded-full transition"
                aria-label="Previous screenshot"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-900 bg-opacity-70 hover:bg-opacity-90 text-white p-2 rounded-full transition"
                aria-label="Next screenshot"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Screenshot Title & Description */}
          <div className="mt-3">
            <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{currentScreenshot.title}</h4>
            {currentScreenshot.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{currentScreenshot.description}</p>
            )}
          </div>

          {/* Indicators */}
          {screenshots.length > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {screenshots.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setCurrentIndex(index);
                      setIsTransitioning(false);
                    }, 300);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex
                    ? 'w-8 bg-blue-500'
                    : 'w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  aria-label={`Go to screenshot ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      <ImageLightbox
        screenshots={screenshots}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default ScreenshotCarousel;

